import React from 'react'
import { ArrowUpRight, Download } from 'react-feather'
import { SingleMenuItem } from '@mprolab/uikit'
import { Network } from '@mprolab/sdk'
import {
  DexToolsProps,
  FooterLinksProps,
  FooterSocialProps,
  HeaderItems,
  RoadmapProps,
} from './types'
export { heroConfig } from './hero-slides'
export { tokenomicsBaseConfig, tokenomicsConfig } from './tokenomics-section'

export const headerConfig: HeaderItems[] = [
  {
    // Phase 3 — NFT marketplace. Public list/detail; listing or buying
    // requires a wallet. First, by operator decision: it is the entry point
    // people arrive for.
    //
    // Labelled "Market" rather than "Marketplace" since scope 40 (operator
    // decision): the row gained a Stocks entry and the longest word is what
    // had to give. The route, the page and the documentation keep the full
    // name — only the navigation is short.
    name: 'Market',
    href: '/marketplace',
    internalLink: true
  },
  {
    // Scope 13 - swap any token on Base through Aerodrome, Uniswap and
    // KyberSwap. It shares a module with the bridge, because the two are one
    // errand: moving value, one across chains and one across tokens. The
    // module is served at /swap — that is what people come for.
    name: 'Swap',
    href: '/swap?tab=swap',
    internalLink: true
  },
  {
    // Scope 40 — the tokenized-stock board. Next to Swap on purpose: it is a
    // shop window for the same router, and the route people arrive on from a
    // search for a ticker rather than for a token.
    name: 'Stocks',
    href: '/stocks',
    internalLink: true
  },
  {
    name: 'Bridge',
    href: '/swap?tab=bridge',
    internalLink: true
  },
  {
    // Phase 4 DAO MVP — internal /dao route. Governance is for licence
    // holders: the gate requires a wallet session AND at least one licence,
    // so nothing here is readable without one. The API's proposal reads are
    // public, the screen is not — this comment used to claim otherwise.
    name: 'DAO',
    href: '/dao',
    internalLink: true
  },
  {
    // Scope 10 — people, clans and the leaderboards. Public to read; acting on
    // a clan needs a wallet session.
    name: 'Community',
    href: '/community',
    internalLink: true
  },
  {
    name: 'Staking',
    href: '/stake',
    internalLink: true
  },
  {
    name: 'MPRO',
    href: '/mpro',
    internalLink: true
  }
  // Documentation is deliberately absent from the header (operator decision).
  // The /documentation route still resolves, so links already handed out keep
  // working — this hides the entry point, it does not delete the content.
]

export const roadmapConfig: RoadmapProps[] = [
  {
    phase: 'Short-Term Goals (6–12 Months)',
    description: 'Ecosystem Expansion',
    color: 'purple',
    roadmapPoints: [
      {
        description: 'Launch Daphne AI agent for user interaction and community support'
      },
      {
        description: 'Expand Fair Launch Hub with meme-coin tools and token automation'
      },
      {
        description: 'Establish MPRO Lab as a central hub for the MPRO community'
      },
      {
        description: 'Launch MGA for participants to earn from game royalties and fees'
      },
      {
        description: 'Establish partnerships with leading franchises and global federations'
      }
    ]
  },
  {
    phase: 'Mid-Term Goals (1–2 Years)',
    description: 'Tech evolution',
    color: 'magenta',
    roadmapPoints: [
      {
        description: 'Launch the blockchain testnet for token and game integration'
      },
      {
        description: 'Scale Fair Launch Hub to 500+ games across Base and Polygon'
      },
      {
        description: 'Advance MGA tools to increase rewards for participants'
      },
      {
        description: 'Onboard top studios and influencers to expand the ecosystem'
      },
      {
        description: 'Enhance NODE rewards with dual-coin incentives'
      }
    ]
  },
  {
    phase: 'Long-Term Goals (2+ Years)',
    description: 'Community Engagement',
    color: 'cyan',
    roadmapPoints: [
      {
        description: 'Grow MPRO Lab reach with MPRO as the core ecosystem currency'
      },
      {
        description: 'Scale the platform to millions of users and thousands of games globally'
      },
      {
        description: 'Establish leadership in decentralized gaming with zkEVM integration'
      },
      {
        description: 'Deepen partnerships with global studios, franchises, and federations.'
      },
      {
        description: 'Expand NODE rewards and gamified user engagement features.'
      }
    ]
  }
]

export const footerConfig: FooterLinksProps[] = []

export const footerSocialsConfig: FooterSocialProps[] = [
  {
    header: 'MPRO LAB',
    socials: [
      {
        name: 'Twitter',
        href: import.meta.env.VITE_MPROLAB_TWITTER_URL
      },
      {
        name: 'Telegram',
        href: import.meta.env.VITE_MPROLAB_TELEGRAM_URL
      },
      {
        name: 'Coinmarketcap',
        href: import.meta.env.VITE_MPROLAB_COINMARKETCAP_URL
      },
      {
        name: 'Coingecko',
        href: import.meta.env.VITE_MPROLAB_COINGECKO_URL
      }
    ],
    dexTools: true
  }
]

export const userPanelConfig: SingleMenuItem[] = [
  {
    /*
     * My profile — first, because it is the only entry that is about the
     * person rather than their holdings.
     *
     * The page had shipped with no link anywhere in the app: the route
     * resolved, the screen worked, and from the outside the feature simply did
     * not exist. A screen nothing points at is not delivered.
     */
    label: 'My Profile',
    endIcon: <ArrowUpRight size={16} />,
    link: '/profile',
    hoverableIcon: true,
    tokenRestricted: true
  },
  {
    /*
     * The full register, reachable from the user menu as well as from the
     * bell. A history that can only be opened from a dropdown is a history
     * nobody keeps — the same reason every back-office surface we build has
     * both entry points.
     */
    label: 'Notifications',
    endIcon: <ArrowUpRight size={16} />,
    link: '/notifications',
    hoverableIcon: true,
    tokenRestricted: true
  },
  {
    // Deep-links into the marketplace's own "My auctions" filter rather than
    // a second screen rendering the same cards from the same data.
    label: 'My Auctions',
    endIcon: <ArrowUpRight size={16} />,
    link: '/marketplace?filter=mine',
    hoverableIcon: true,
    tokenRestricted: true
  },
  {
    label: 'Licenses',
    endIcon: <ArrowUpRight size={16} />,
    link: '/user-licenses',
    hoverableIcon: true,
    tokenRestricted: true
  },
  {
    // The token distribution, not a reward the platform hands out — the same
    // wording the whole rewards surface now uses.
    label: 'Distribution',
    endIcon: <ArrowUpRight size={16} />,
    link: '/rewards',
    hoverableIcon: true,
    tokenRestricted: true
  },
  {
    label: 'Storage',
    endIcon: <ArrowUpRight size={16} />,
    link: '/nodes-storage',
    hoverableIcon: true,
    tokenRestricted: true
  },
  {
    /*
     * Terminal — the desktop app (documentation/specification/TERMINAL.md).
     *
     * Second to last by operator decision (2026-09-05), separated from the
     * entries above because it is not another screen of this app: it is where
     * you leave the browser and install something. Not `tokenRestricted`:
     * reading what Terminal is and downloading it needs no signature — the
     * licence is checked by Terminal itself, at binding.
     */
    label: 'Terminal App',
    icon: <Download size={16} />,
    endIcon: <ArrowUpRight size={16} />,
    link: '/terminal',
    hoverableIcon: true,
    divider: true
  }
]

export const dexToolsConfig: DexToolsProps[] = [
  {
    id: 'Dextools',
    networks: [
      { network: Network.BASE, href: import.meta.env.VITE_DEX_TOOLS_BASE_MPRO_URL },
      { network: Network.ETHEREUM, href: import.meta.env.VITE_DEX_TOOLS_ETH_MPRO_URL }
    ]
  },
  {
    id: 'Dexscreen',
    networks: [{ network: Network.BASE, href: import.meta.env.VITE_DEX_SCREENER_BASE_MPRO_URL }]
  }
]

export const MAX_AVATAR_UPLOAD_SIZE = 2
export const MIN_USERNAME_LENGTH = 2
export const MAX_USERNAME_LENGTH = 100

export const MAX_FIRST_NAME_LENGHT = 50
export const MAX_LAST_NAME_LENGHT = 50

export const MAX_DATE = new Date()

export const MAX_DESCRIPTION_LENGTH = 3000
